import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const TaxSettingsTab = () => {
  const [taxSettings, setTaxSettings] = useState({
    defaultVatRate: "15",
    zeroRatedEnabled: true,
    exemptEnabled: true,
    customRates: [
      { id: 1, name: "Standard VAT", rate: "15", isDefault: true, enabled: true },
      { id: 2, name: "Zero Rated", rate: "0", isDefault: false, enabled: true },
      { id: 3, name: "Exempt", rate: "0", isDefault: false, enabled: true }
    ],
    exemptionCategories: [
      { id: 1, name: "Medical Services", code: "MEDICAL", enabled: true },
      { id: 2, name: "Educational Services", code: "EDUCATION", enabled: true },
      { id: 3, name: "Financial Services", code: "FINANCIAL", enabled: false },
      { id: 4, name: "Real Estate", code: "REALESTATE", enabled: true }
    ],
    complianceRules: {
      requireTaxId: true,
      validateVatNumber: true,
      enforceThresholds: true,
      autoCalculate: true
    }
  });

  const [newRate, setNewRate] = useState({ name: '', rate: '', enabled: true });
  const [isLoading, setIsLoading] = useState(false);

  const handleSettingChange = (field, value) => {
    setTaxSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleComplianceRuleChange = (rule, value) => {
    setTaxSettings(prev => ({
      ...prev,
      complianceRules: {
        ...prev?.complianceRules,
        [rule]: value
      }
    }));
  };

  const handleRateChange = (id, field, value) => {
    setTaxSettings(prev => ({
      ...prev,
      customRates: prev?.customRates?.map(rate =>
        rate?.id === id ? { ...rate, [field]: value } : rate
      )
    }));
  };

  const handleExemptionChange = (id, enabled) => {
    setTaxSettings(prev => ({
      ...prev,
      exemptionCategories: prev?.exemptionCategories?.map(category =>
        category?.id === id ? { ...category, enabled } : category
      )
    }));
  };

  const addCustomRate = () => {
    if (newRate?.name && newRate?.rate) {
      const newId = Math.max(...taxSettings?.customRates?.map(r => r?.id)) + 1;
      setTaxSettings(prev => ({
        ...prev,
        customRates: [...prev?.customRates, {
          id: newId,
          name: newRate?.name,
          rate: newRate?.rate,
          isDefault: false,
          enabled: newRate?.enabled
        }]
      }));
      setNewRate({ name: '', rate: '', enabled: true });
    }
  };

  const removeCustomRate = (id) => {
    setTaxSettings(prev => ({
      ...prev,
      customRates: prev?.customRates?.filter(rate => rate?.id !== id && !rate?.isDefault)
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Tax settings saved:', taxSettings);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground">Tax Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure tax rates, exemptions, and compliance rules for Saudi Arabia
        </p>
      </div>
      {/* Default Tax Rates */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Calculator" size={20} className="mr-2 text-primary" />
            Default Tax Rates
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Default VAT Rate (%)"
              type="number"
              required
              value={taxSettings?.defaultVatRate}
              onChange={(e) => handleSettingChange('defaultVatRate', e?.target?.value)}
              min="0"
              max="100"
              step="0.01"
              description="Standard VAT rate for Saudi Arabia"
            />
            
            <div className="flex items-center space-x-4 pt-6">
              <Checkbox
                label="Enable Zero-Rated"
                checked={taxSettings?.zeroRatedEnabled}
                onChange={(e) => handleSettingChange('zeroRatedEnabled', e?.target?.checked)}
              />
              <Checkbox
                label="Enable Exempt"
                checked={taxSettings?.exemptEnabled}
                onChange={(e) => handleSettingChange('exemptEnabled', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        {/* Custom Tax Rates */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Settings" size={20} className="mr-2 text-primary" />
            Custom Tax Rates
          </h3>
          
          <div className="space-y-3">
            {taxSettings?.customRates?.map((rate) => (
              <div key={rate?.id} className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div className="flex items-center space-x-4">
                  <Checkbox
                    checked={rate?.enabled}
                    onChange={(e) => handleRateChange(rate?.id, 'enabled', e?.target?.checked)}
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">{rate?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {rate?.rate}% {rate?.isDefault && '(Default)'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-primary">{rate?.rate}%</span>
                  {!rate?.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCustomRate(rate?.id)}
                      iconName="Trash2"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Add New Rate */}
          <div className="mt-4 p-4 border-2 border-dashed border-border rounded-xl">
            <h4 className="text-sm font-medium text-foreground mb-3">Add Custom Rate</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="Rate Name"
                type="text"
                value={newRate?.name}
                onChange={(e) => setNewRate(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder="e.g., Luxury Tax"
              />
              <Input
                label="Rate (%)"
                type="number"
                value={newRate?.rate}
                onChange={(e) => setNewRate(prev => ({ ...prev, rate: e?.target?.value }))}
                placeholder="0.00"
                min="0"
                max="100"
                step="0.01"
              />
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={addCustomRate}
                  iconName="Plus"
                  iconPosition="left"
                  disabled={!newRate?.name || !newRate?.rate}
                >
                  Add Rate
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Exemption Categories */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Shield" size={20} className="mr-2 text-primary" />
            Tax Exemption Categories
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {taxSettings?.exemptionCategories?.map((category) => (
              <div key={category?.id} className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    checked={category?.enabled}
                    onChange={(e) => handleExemptionChange(category?.id, e?.target?.checked)}
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">{category?.name}</p>
                    <p className="text-xs text-muted-foreground">Code: {category?.code}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {category?.enabled ? (
                    <Icon name="CheckCircle" size={16} className="text-success" />
                  ) : (
                    <Icon name="XCircle" size={16} className="text-muted-foreground" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Rules */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="FileCheck" size={20} className="mr-2 text-primary" />
            Compliance Rules
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Require Tax ID</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Mandate tax identification for all transactions
                  </p>
                </div>
                <Checkbox
                  checked={taxSettings?.complianceRules?.requireTaxId}
                  onChange={(e) => handleComplianceRuleChange('requireTaxId', e?.target?.checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Validate VAT Numbers</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Verify VAT number format and validity
                  </p>
                </div>
                <Checkbox
                  checked={taxSettings?.complianceRules?.validateVatNumber}
                  onChange={(e) => handleComplianceRuleChange('validateVatNumber', e?.target?.checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Enforce Thresholds</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Apply ZATCA registration thresholds
                  </p>
                </div>
                <Checkbox
                  checked={taxSettings?.complianceRules?.enforceThresholds}
                  onChange={(e) => handleComplianceRuleChange('enforceThresholds', e?.target?.checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Auto-Calculate Tax</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Automatically calculate tax amounts
                  </p>
                </div>
                <Checkbox
                  checked={taxSettings?.complianceRules?.autoCalculate}
                  onChange={(e) => handleComplianceRuleChange('autoCalculate', e?.target?.checked)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="AlertTriangle" size={16} className="text-warning" />
          <span>Tax setting changes affect all future invoices</span>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => window.location?.reload()}>
            Reset Changes
          </Button>
          <Button 
            variant="default" 
            loading={isLoading}
            onClick={handleSave}
            iconName="Save"
            iconPosition="left"
          >
            Save Tax Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaxSettingsTab;

